import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SheetConfigUpdater from './components/SheetConfigUpdater';
import { MONTHS } from './constants';

function FinalizeMonth({ setSummaryData, refreshData }) {
  const [csvFile, setCsvFile] = useState(null);
  const [income, setIncome] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfigUpdater, setShowConfigUpdater] = useState(false);

  // Preview state
  const [preview, setPreview] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  // State for finalization method selection
  const [finalizationMethod, setFinalizationMethod] = useState('current'); // 'current', 'previous', 'manual'
  const [previousSheetConfigs, setPreviousSheetConfigs] = useState([]);
  const [selectedPreviousConfig, setSelectedPreviousConfig] = useState(null);
  const [manualSheetId, setManualSheetId] = useState('');
  const [manualTabName, setManualTabName] = useState('');
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(true);

  // State for CSV upload sheet selection
  const [csvUploadMethod, setCsvUploadMethod] = useState('current'); // 'current', 'previous', 'manual'
  const [csvSelectedPreviousConfig, setCsvSelectedPreviousConfig] = useState(null);
  const [csvManualSheetId, setCsvManualSheetId] = useState('');
  const [csvManualTabName, setCsvManualTabName] = useState('');

  // Month picker state
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  // Prepare month and year options
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({length: 5}, (_, i) => currentYear - i); // Current year and 4 years back

  // Load previous sheet configurations and set default month/year
  useEffect(() => {
    // Set default month to previous month
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    setSelectedMonth(months[prevMonth.getMonth()]);
    setSelectedYear(prevMonth.getFullYear().toString());

    const fetchSheetConfigs = async () => {
      try {
        setIsLoadingConfigs(true);
        const response = await axios.get('/api/sheet-config-history', { withCredentials: true });
        if (response.data.success && response.data.previousConfigs) {
          // Filter to only show unfinalized configs
          const unfinalizedConfigs = response.data.previousConfigs
            .filter(config => !config.finalized)
            .sort((a, b) => new Date(b.date) - new Date(a.date));

          setPreviousSheetConfigs(unfinalizedConfigs);

          // If there are unfinalized configs, default to using the most recent one
          if (unfinalizedConfigs.length > 0) {
            setSelectedPreviousConfig(unfinalizedConfigs[0]);
            setFinalizationMethod('previous');
          }
        }
      } catch (err) {
        console.error('Failed to load sheet configurations:', err);
      } finally {
        setIsLoadingConfigs(false);
      }
    };

    fetchSheetConfigs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear preview when sheet selection or month/year changes
  useEffect(() => {
    setPreview(null);
  }, [finalizationMethod, selectedPreviousConfig, manualSheetId, manualTabName, selectedMonth, selectedYear]);

  const handleCsvUpload = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      setMessage('Please select a CSV file.');
      return;
    }

    // Validate sheet selection based on the chosen method
    if (csvUploadMethod === 'previous' && !csvSelectedPreviousConfig) {
      setMessage('Please select a previous sheet configuration for CSV upload.');
      return;
    }

    if (csvUploadMethod === 'manual' && (!csvManualSheetId || !csvManualTabName)) {
      setMessage('Please enter both Sheet ID and Tab Name for manual CSV upload.');
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append('csv', csvFile);

    // Add sheet target information based on selected method
    if (csvUploadMethod === 'current') {
      formData.append('useCurrentSheet', 'true');
    } else if (csvUploadMethod === 'previous' && csvSelectedPreviousConfig) {
      formData.append('targetSheetId', csvSelectedPreviousConfig.sheetId);
      formData.append('targetTabName', csvSelectedPreviousConfig.tabName);
    } else if (csvUploadMethod === 'manual') {
      formData.append('targetSheetId', csvManualSheetId);
      formData.append('targetTabName', csvManualTabName);
    }

    try {
      // Use consistent URL handling - the axios interceptor will add the base URL
      const response = await axios.post('/api/upload-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      if (response.data.success) {
        const details = response.data.details || {};
        setMessage(`CSV data (${details.recordCount || 'unknown'} records) uploaded successfully to ${details.tabName || 'the selected sheet'}`);
      } else {
        setMessage(response.data.message || 'CSV uploaded successfully');
      }

      refreshData(`${MONTHS[new Date().getMonth()]} ${new Date().getFullYear()}`);
    } catch (err) {
      setMessage('CSV upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  const buildRequestData = () => {
    const requestData = {
      month: `${selectedMonth} ${selectedYear}`
    };

    if (finalizationMethod === 'current') {
      requestData.useCurrentSheet = true;
    } else if (finalizationMethod === 'previous' && selectedPreviousConfig) {
      requestData.sheetId = selectedPreviousConfig.sheetId;
      requestData.tabName = selectedPreviousConfig.tabName;
    } else if (finalizationMethod === 'manual') {
      requestData.sheetId = manualSheetId;
      requestData.tabName = manualTabName;
    }

    return requestData;
  };

  const handlePreview = async () => {
    // Validate other required fields based on finalization method
    if (finalizationMethod === 'manual' && (!manualSheetId || !manualTabName)) {
      setMessage('Please enter both Sheet ID and Tab Name for manual finalization.');
      return;
    }

    if (finalizationMethod === 'previous' && !selectedPreviousConfig) {
      setMessage('Please select a previous sheet configuration to finalize.');
      return;
    }

    // Validate month and year selection
    if (!selectedMonth || !selectedYear) {
      setMessage('Please select both month and year to finalize.');
      return;
    }

    setIsPreviewLoading(true);
    setMessage('');

    try {
      const requestData = buildRequestData();
      requestData.dryRun = true;

      const response = await axios.post('/api/finalize-month', requestData, { withCredentials: true });

      // Flatten the dryRun response for easier template access
      const data = response.data;
      setPreview({
        expenseCount: data.expenseCount,
        total: data.summary?.total,
        danielsTotal: data.summary?.danielsTotal,
        yehuditsTotal: data.summary?.yehuditsTotal,
        categoryCount: data.summary?.categories,
        sheetIncome: data.sheetIncome,
        alreadyFinalized: data.alreadyFinalized,
        finalizedDate: data.existingData?.finalizedAt,
        previousTotal: data.existingData?.total
      });
      // Pre-fill income from sheet data
      if (data.sheetIncome != null) {
        setIncome(String(data.sheetIncome));
      } else {
        setIncome('');
      }
    } catch (err) {
      setMessage('Preview failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleFinalize = async () => {
    // Validate income
    if (income === '') {
      setMessage('Please enter income amount (0 is allowed).');
      return;
    }

    if (isNaN(parseFloat(income)) || parseFloat(income) < 0) {
      setMessage('Please enter a valid number for income (0 or more).');
      return;
    }

    // Add confirmation dialog
    const confirmMessage = `Are you sure you want to finalize ${selectedMonth} ${selectedYear}? This will archive all spending data and cannot be easily undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsLoading(true);
    const finalizedMonth = `${selectedMonth} ${selectedYear}`;

    try {
      const requestData = buildRequestData();
      requestData.income = parseFloat(income);

      // If already finalized, send force flag
      if (preview && preview.alreadyFinalized) {
        requestData.force = true;
      }

      const response = await axios.post('/api/finalize-month', requestData, { withCredentials: true });

      setMessage(response.data.message);
      setCsvFile(null);
      setIncome('');
      setPreview(null);

      // Refresh data on the frontend
      refreshData(finalizedMonth);

      // Refresh the sheet config list instead of optimistic updates
      try {
        const configResponse = await axios.get('/api/sheet-config-history', { withCredentials: true });
        if (configResponse.data.success && configResponse.data.previousConfigs) {
          // Filter to only show unfinalized configs
          const unfinalizedConfigs = configResponse.data.previousConfigs
            .filter(config => !config.finalized)
            .sort((a, b) => new Date(b.date) - new Date(a.date));

          setPreviousSheetConfigs(unfinalizedConfigs);
          setSelectedPreviousConfig(null);

          // If there are no more unfinalized configs, switch to current method
          if (unfinalizedConfigs.length === 0) {
            setFinalizationMethod('current');
          }
        }
      } catch (refreshErr) {
        console.error('Failed to refresh config list:', refreshErr);
      }
    } catch (err) {
      setMessage('Finalize failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount == null) return '---';
    return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  };

  return (
    <div className="min-h-screen p-4 md:p-6">
      <div className="w-full max-w-5xl mx-auto">
        <header className="mb-6 bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl shadow-lg p-6 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Budget Management</h1>
          <p className="text-white/70">Upload CSV, finalize months, and manage sheet settings</p>

          {/* Sheet Configuration Toggle Button */}
          <div className="mt-4 flex justify-center">
            <button
              onClick={() => setShowConfigUpdater(!showConfigUpdater)}
              className={`px-4 py-2 text-sm font-medium rounded-lg backdrop-blur-sm border transition-all duration-200 ${
                showConfigUpdater
                  ? 'bg-purple-500/20 text-purple-200 border-purple-400/30 hover:bg-purple-500/30'
                  : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
              }`}
            >
              {showConfigUpdater ? 'Hide' : 'Show'} Sheet Configuration Manager
            </button>
          </div>
        </header>

        {/* Conditional Sheet Configuration Updater */}
        {showConfigUpdater && (
          <div className="mb-6">
            <SheetConfigUpdater />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* CSV Upload Section */}
          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:bg-white/15">
            <div className="p-1 bg-blue-500/30 backdrop-blur-sm"></div>
            <div className="p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Upload Spending CSV</h3>
              <form onSubmit={handleCsvUpload} className="space-y-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-white/90 mb-2">Select CSV File</label>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setCsvFile(e.target.files[0])}
                      className="block w-full text-sm text-white/70 bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg p-2
                      file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium
                      file:bg-blue-500/20 file:text-blue-200 hover:file:bg-blue-500/30 file:backdrop-blur-sm
                      cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400/50"
                    />
                  </div>
                  <p className="mt-1 text-xs text-white/60">{csvFile ? `Selected: ${csvFile.name}` : "No file selected"}</p>
                </div>

                {/* CSV Upload Sheet Selection */}
                <div className="mt-4">
                  <fieldset className="border border-white/20 bg-white/5 backdrop-blur-sm rounded-lg p-4">
                    <legend className="text-sm font-medium text-white/90 px-2">Select Upload Target Sheet</legend>

                    <div className="space-y-3">
                      {/* Current Sheet Option */}
                      <div className="flex items-center">
                        <input
                          id="csv-current-sheet"
                          name="csv-upload-method"
                          type="radio"
                          checked={csvUploadMethod === 'current'}
                          onChange={() => setCsvUploadMethod('current')}
                          className="h-4 w-4 text-blue-400 focus:ring-blue-400/50 border-white/30 bg-white/10"
                        />
                        <label htmlFor="csv-current-sheet" className="ml-2 block text-sm text-white/90">
                          Upload to current active Sheet
                        </label>
                      </div>

                      {/* Previous Sheet Option */}
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="csv-previous-sheet"
                            name="csv-upload-method"
                            type="radio"
                            checked={csvUploadMethod === 'previous'}
                            onChange={() => setCsvUploadMethod('previous')}
                            disabled={previousSheetConfigs.length === 0}
                            className="h-4 w-4 text-blue-400 focus:ring-blue-400/50 border-white/30 bg-white/10"
                          />
                        </div>
                        <div className="ml-2 text-sm">
                          <label htmlFor="csv-previous-sheet" className={`font-medium ${previousSheetConfigs.length === 0 ? 'text-white/40' : 'text-white/90'}`}>
                            Upload to previous Sheet
                          </label>

                          {csvUploadMethod === 'previous' && previousSheetConfigs.length > 0 && (
                            <div className="mt-2">
                              <select
                                value={csvSelectedPreviousConfig ? JSON.stringify({
                                  sheetId: csvSelectedPreviousConfig.sheetId,
                                  tabName: csvSelectedPreviousConfig.tabName
                                }) : ''}
                                onChange={(e) => {
                                  if (e.target.value) {
                                    const selected = JSON.parse(e.target.value);
                                    setCsvSelectedPreviousConfig(selected);
                                  } else {
                                    setCsvSelectedPreviousConfig(null);
                                  }
                                }}
                                className="block w-full py-2 px-3 border border-white/30 bg-gray-800/90 text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 text-sm cursor-pointer hover:bg-gray-700/90"
                                style={{
                                  WebkitAppearance: 'none',
                                  MozAppearance: 'none',
                                  appearance: 'none',
                                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23ffffff'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                  backgroundRepeat: 'no-repeat',
                                  backgroundPosition: 'right 0.5rem center',
                                  backgroundSize: '1.5em 1.5em',
                                  paddingRight: '2.5rem'
                                }}
                              >
                                <option value="" style={{backgroundColor: '#1f2937', color: '#fff'}}>Select a configuration...</option>
                                {previousSheetConfigs.map((config, idx) => (
                                  <option key={idx} value={JSON.stringify({
                                    sheetId: config.sheetId,
                                    tabName: config.tabName
                                  })} style={{backgroundColor: '#1f2937', color: '#fff'}}>
                                    {config.tabName} ({new Date(config.date).toLocaleDateString()})
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          {previousSheetConfigs.length === 0 && (
                            <p className="text-white/40 text-xs mt-1">No previous sheets available</p>
                          )}
                        </div>
                      </div>

                      {/* Manual Sheet Option */}
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="csv-manual-sheet"
                            name="csv-upload-method"
                            type="radio"
                            checked={csvUploadMethod === 'manual'}
                            onChange={() => setCsvUploadMethod('manual')}
                            className="h-4 w-4 text-blue-400 focus:ring-blue-400/50 border-white/30 bg-white/10"
                          />
                        </div>
                        <div className="ml-2 text-sm">
                          <label htmlFor="csv-manual-sheet" className="font-medium text-white/90">
                            Specify Sheet manually
                          </label>

                          {csvUploadMethod === 'manual' && (
                            <div className="mt-2 space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-white/70 mb-1">Sheet ID</label>
                                <input
                                  type="text"
                                  value={csvManualSheetId}
                                  onChange={(e) => setCsvManualSheetId(e.target.value)}
                                  className="block w-full py-2 px-3 border border-white/30 bg-gray-800/90 text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 text-sm placeholder-gray-400 hover:bg-gray-700/90"
                                  placeholder="e.g., 1ZRCJh8VUXXr0iBL02I4iTDVuqjexPg8U4ja0njNKYW4"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-white/70 mb-1">Tab Name</label>
                                <input
                                  type="text"
                                  value={csvManualTabName}
                                  onChange={(e) => setCsvManualTabName(e.target.value)}
                                  className="block w-full py-2 px-3 border border-white/30 bg-gray-800/90 text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 text-sm placeholder-gray-400 hover:bg-gray-700/90"
                                  placeholder="e.g., Expenses April"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </fieldset>
                </div>

                <div className="bg-blue-500/20 backdrop-blur-sm rounded-lg p-3 mt-2 border border-blue-400/30">
                  <div className="space-y-2">
                    <p className="text-sm text-blue-200 font-medium">
                      Upload your CSV file to the selected Google Sheet.
                    </p>
                    <p className="text-xs text-blue-300">
                      <span className="font-semibold">CSV Format:</span> Your CSV can now use two date formats:
                    </p>
                    <ul className="list-disc list-inside text-xs text-blue-300 space-y-1 ml-2">
                      <li><span className="font-medium">Simple format:</span> Just the day number (e.g., "5") - month will be taken from the sheet</li>
                      <li><span className="font-medium">Original format:</span> Day-Month (e.g., "5-Apr") - specific month override</li>
                    </ul>
                    <p className="text-xs text-blue-300 italic">
                      The system automatically extracts the month from your selected sheet name (e.g., "Expenses April").
                    </p>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !csvFile ||
                    (csvUploadMethod === 'previous' && !csvSelectedPreviousConfig) ||
                    (csvUploadMethod === 'manual' && (!csvManualSheetId || !csvManualTabName))}
                  className="w-full py-3 px-4 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 hover:bg-blue-500/30 text-white font-medium rounded-lg
                  transition-all duration-200 flex justify-center items-center disabled:bg-white/10 disabled:border-white/20 disabled:text-white/50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Uploading...
                    </>
                  ) : "Upload CSV File"}
                </button>
              </form>
            </div>
          </div>

          {/* Finalize Month Section */}
          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:bg-white/15">
            <div className="p-1 bg-green-500/30 backdrop-blur-sm"></div>
            <div className="p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Finalize Previous Month</h3>
              <div className="space-y-4">
                {/* Month and Year Pickers */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-white/90 mb-2">Month</label>
                    <select
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="w-full px-4 py-2 border border-white/30 bg-gray-800/90 text-white rounded-lg focus:ring-2 focus:ring-green-400/50 focus:border-green-400/50 focus:outline-none transition duration-200 cursor-pointer hover:bg-gray-700/90"
                      style={{
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        appearance: 'none',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23ffffff'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.5rem center',
                        backgroundSize: '1.5em 1.5em',
                        paddingRight: '2.5rem'
                      }}
                    >
                      <option value="" style={{backgroundColor: '#1f2937', color: '#fff'}}>Select Month</option>
                      {months.map(month => (
                        <option key={month} value={month} style={{backgroundColor: '#1f2937', color: '#fff'}}>{month}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/90 mb-2">Year</label>
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      className="w-full px-4 py-2 border border-white/30 bg-gray-800/90 text-white rounded-lg focus:ring-2 focus:ring-green-400/50 focus:border-green-400/50 focus:outline-none transition duration-200 cursor-pointer hover:bg-gray-700/90"
                      style={{
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        appearance: 'none',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23ffffff'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.5rem center',
                        backgroundSize: '1.5em 1.5em',
                        paddingRight: '2.5rem'
                      }}
                    >
                      <option value="" style={{backgroundColor: '#1f2937', color: '#fff'}}>Select Year</option>
                      {yearOptions.map(year => (
                        <option key={year} value={year} style={{backgroundColor: '#1f2937', color: '#fff'}}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Data source selection */}
                <div className="mt-4">
                  <fieldset className="border border-white/20 bg-white/5 backdrop-blur-sm rounded-lg p-4">
                    <legend className="text-sm font-medium text-white/90 px-2">Select Sheet to Finalize</legend>

                    <div className="space-y-3">
                      {/* Current Sheet Option */}
                      <div className="flex items-center">
                        <input
                          id="current-sheet"
                          name="finalization-method"
                          type="radio"
                          checked={finalizationMethod === 'current'}
                          onChange={() => setFinalizationMethod('current')}
                          className="h-4 w-4 text-green-400 focus:ring-green-400/50 border-white/30 bg-white/10"
                        />
                        <label htmlFor="current-sheet" className="ml-2 block text-sm text-white/90">
                          Use current active Sheet
                        </label>
                      </div>

                      {/* Previous Sheet Option */}
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="previous-sheet"
                            name="finalization-method"
                            type="radio"
                            checked={finalizationMethod === 'previous'}
                            onChange={() => setFinalizationMethod('previous')}
                            disabled={previousSheetConfigs.length === 0}
                            className="h-4 w-4 text-green-400 focus:ring-green-400/50 border-white/30 bg-white/10"
                          />
                        </div>
                        <div className="ml-2 text-sm">
                          <label htmlFor="previous-sheet" className={`font-medium ${previousSheetConfigs.length === 0 ? 'text-white/40' : 'text-white/90'}`}>
                            Use previous unfinalized Sheet
                          </label>

                          {finalizationMethod === 'previous' && previousSheetConfigs.length > 0 && (
                            <div className="mt-2">
                              <select
                                value={selectedPreviousConfig ? JSON.stringify({
                                  sheetId: selectedPreviousConfig.sheetId,
                                  tabName: selectedPreviousConfig.tabName
                                }) : ''}
                                onChange={(e) => {
                                  if (e.target.value) {
                                    const selected = JSON.parse(e.target.value);
                                    setSelectedPreviousConfig(selected);
                                  } else {
                                    setSelectedPreviousConfig(null);
                                  }
                                }}
                                className="block w-full py-2 px-3 border border-white/30 bg-gray-800/90 text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-400/50 text-sm cursor-pointer hover:bg-gray-700/90"
                                style={{
                                  WebkitAppearance: 'none',
                                  MozAppearance: 'none',
                                  appearance: 'none',
                                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23ffffff'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                  backgroundRepeat: 'no-repeat',
                                  backgroundPosition: 'right 0.5rem center',
                                  backgroundSize: '1.5em 1.5em',
                                  paddingRight: '2.5rem'
                                }}
                              >
                                <option value="" style={{backgroundColor: '#1f2937', color: '#fff'}}>Select a configuration...</option>
                                {previousSheetConfigs.map((config, idx) => (
                                  <option key={idx} value={JSON.stringify({
                                    sheetId: config.sheetId,
                                    tabName: config.tabName
                                  })} style={{backgroundColor: '#1f2937', color: '#fff'}}>
                                    {config.tabName} ({new Date(config.date).toLocaleDateString()})
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          {previousSheetConfigs.length === 0 && (
                            <p className="text-white/40 text-xs mt-1">No unfinalized previous sheets available</p>
                          )}
                        </div>
                      </div>

                      {/* Manual Sheet Option */}
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="manual-sheet"
                            name="finalization-method"
                            type="radio"
                            checked={finalizationMethod === 'manual'}
                            onChange={() => setFinalizationMethod('manual')}
                            className="h-4 w-4 text-green-400 focus:ring-green-400/50 border-white/30 bg-white/10"
                          />
                        </div>
                        <div className="ml-2 text-sm">
                          <label htmlFor="manual-sheet" className="font-medium text-white/90">
                            Specify Sheet manually
                          </label>

                          {finalizationMethod === 'manual' && (
                            <div className="mt-2 space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-white/70 mb-1">Sheet ID</label>
                                <input
                                  type="text"
                                  value={manualSheetId}
                                  onChange={(e) => setManualSheetId(e.target.value)}
                                  className="block w-full py-2 px-3 border border-white/30 bg-gray-800/90 text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-400/50 text-sm placeholder-gray-400 hover:bg-gray-700/90"
                                  placeholder="e.g., 1ZRCJh8VUXXr0iBL02I4iTDVuqjexPg8U4ja0njNKYW4"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-white/70 mb-1">Tab Name</label>
                                <input
                                  type="text"
                                  value={manualTabName}
                                  onChange={(e) => setManualTabName(e.target.value)}
                                  className="block w-full py-2 px-3 border border-white/30 bg-gray-800/90 text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-400/50 text-sm placeholder-gray-400 hover:bg-gray-700/90"
                                  placeholder="e.g., Expenses April"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </fieldset>
                </div>

                {/* Info hint before preview */}
                {!preview && (
                  <div className="bg-green-500/20 backdrop-blur-sm rounded-lg p-3 mt-2 border border-green-400/30">
                    <p className="text-sm text-green-200">
                      Preview the finalization data from the selected sheet
                      for <span className="font-semibold">{selectedMonth} {selectedYear}</span> before confirming.
                    </p>
                  </div>
                )}

                {/* Preview Button (shown when no preview loaded yet) */}
                {!preview && (
                  <button
                    type="button"
                    onClick={handlePreview}
                    disabled={isPreviewLoading || !selectedMonth || !selectedYear ||
                      (finalizationMethod === 'manual' && (!manualSheetId || !manualTabName)) ||
                      (finalizationMethod === 'previous' && !selectedPreviousConfig)}
                    className="w-full py-3 px-4 bg-green-500/20 backdrop-blur-sm border border-green-400/30 hover:bg-green-500/30 text-white font-medium rounded-lg
                    transition-all duration-200 flex justify-center items-center disabled:bg-white/10 disabled:border-white/20 disabled:text-white/50 disabled:cursor-not-allowed"
                  >
                    {isPreviewLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading Preview...
                      </>
                    ) : "Preview Finalization"}
                  </button>
                )}

                {/* Preview Panel (shown after dry run completes) */}
                {preview && (
                  <div className="space-y-4">
                    {/* Already Finalized Warning */}
                    {preview.alreadyFinalized && (
                      <div className="bg-amber-500/20 backdrop-blur-sm rounded-lg p-4 border border-amber-400/30">
                        <h4 className="text-sm font-semibold text-amber-200 mb-1">Warning: Already Finalized</h4>
                        <p className="text-sm text-amber-200">
                          {selectedMonth} {selectedYear} was already finalized
                          {preview.finalizedDate ? ` on ${new Date(preview.finalizedDate).toLocaleDateString()}` : ''}.
                          {preview.previousTotal != null ? ` Previous total: ${formatCurrency(preview.previousTotal)}.` : ''}
                          {' '}Finalizing again will overwrite the existing data.
                        </p>
                      </div>
                    )}

                    {/* Preview Summary Card */}
                    <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-4 space-y-3">
                      <h4 className="text-sm font-semibold text-white/90 uppercase tracking-wide">Finalization Preview</h4>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                          <p className="text-xs text-white/60 mb-1">Expenses Found</p>
                          <p className="text-lg font-semibold text-white">
                            {preview.expenseCount != null ? preview.expenseCount : '---'}
                          </p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                          <p className="text-xs text-white/60 mb-1">Total Spending</p>
                          <p className="text-lg font-semibold text-white">
                            {formatCurrency(preview.total)}
                          </p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                          <p className="text-xs text-white/60 mb-1">Daniel's Total</p>
                          <p className="text-lg font-semibold text-white">
                            {formatCurrency(preview.danielsTotal)}
                          </p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                          <p className="text-xs text-white/60 mb-1">Yehudit's Total</p>
                          <p className="text-lg font-semibold text-white">
                            {formatCurrency(preview.yehuditsTotal)}
                          </p>
                        </div>
                      </div>

                      {preview.categoryCount != null && (
                        <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                          <p className="text-xs text-white/60 mb-1">Categories</p>
                          <p className="text-sm text-white">
                            {preview.categoryCount} categories found
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Income Input (editable, pre-filled from sheet) */}
                    <div>
                      <label className="block text-sm font-medium text-white/90 mb-2">Month Income (ILS)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={income}
                        onChange={(e) => setIncome(e.target.value)}
                        className="w-full px-4 py-2 border border-white/30 bg-gray-800/90 text-white rounded-lg focus:ring-2 focus:ring-green-400/50 focus:border-green-400/50 focus:outline-none transition duration-200 placeholder-gray-400 hover:bg-gray-700/90"
                        placeholder="e.g., 112038.50"
                      />
                      <p className="mt-1 text-xs text-white/60">
                        {preview.sheetIncome != null
                          ? `Pre-filled from sheet income. Edit if needed.`
                          : 'No income found on sheet. Enter the total income for this month.'}
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => { setPreview(null); setIncome(''); }}
                        className="flex-1 py-3 px-4 bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 text-white font-medium rounded-lg
                        transition-all duration-200 flex justify-center items-center"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleFinalize}
                        disabled={isLoading || income === '' || isNaN(parseFloat(income)) || parseFloat(income) < 0}
                        className="flex-1 py-3 px-4 bg-green-500/20 backdrop-blur-sm border border-green-400/30 hover:bg-green-500/30 text-white font-medium rounded-lg
                        transition-all duration-200 flex justify-center items-center disabled:bg-white/10 disabled:border-white/20 disabled:text-white/50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Finalizing...
                          </>
                        ) : "Confirm & Finalize"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Status Messages */}
        {message && (
          <div className={`mt-6 p-4 rounded-xl backdrop-blur-lg border transition-all duration-300 animate-fadeIn shadow-lg ${
            message.includes('failed') || message.includes('Error')
              ? 'bg-red-500/20 border-red-400/30 text-red-200'
              : 'bg-green-500/20 border-green-400/30 text-green-200'
          }`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {message.includes('failed') || message.includes('Error') ? (
                  <svg className="h-5 w-5 text-red-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-green-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">{message}</p>
              </div>
            </div>
          </div>
        )}

        {/* Instructions Card */}
        <div className="mt-6 bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl shadow-lg p-6 border-l-4 border-blue-400/50">
          <h3 className="text-lg font-semibold text-white mb-2">Budget Management Guide</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <div>
              <h4 className="font-medium text-white/90 mb-2">Sheet Configuration</h4>
              <ul className="list-disc list-inside space-y-1 text-white/70 text-sm">
                <li>Use <strong>Sheet Configuration Manager</strong> to update the Google Sheet ID and tab name</li>
                <li>Update the sheet configuration at the start of a new month</li>
                <li>This immediately affects where new expense entries are recorded</li>
                <li>The system remembers previous sheet configurations for later finalization</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-white/90 mb-2">Month Finalization</h4>
              <ul className="list-disc list-inside space-y-1 text-white/70 text-sm">
                <li>Select the <strong>month and year</strong> you're finalizing</li>
                <li>Choose the sheet to finalize from:
                  <ul className="ml-5 list-disc text-xs space-y-0.5 mt-1">
                    <li>Current active sheet</li>
                    <li>Previous unfinalized sheets</li>
                    <li>Manual sheet specification</li>
                  </ul>
                </li>
                <li>Click <strong>Preview Finalization</strong> to see a summary before committing</li>
                <li>Review the expense count, totals, and income from the sheet</li>
                <li>Adjust the income if needed, then confirm to archive the data</li>
              </ul>
            </div>
          </div>

          <div className="bg-blue-500/20 backdrop-blur-sm p-3 rounded-lg mt-4 border border-blue-400/30">
            <h4 className="text-sm font-medium text-blue-200 mb-1">Recommended Workflow</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-blue-300">
              <li>When a new month starts, update your Sheet configuration first</li>
              <li>Start entering expenses for the new month in the new sheet</li>
              <li>Later, finalize the previous month by selecting it from the "previous sheets" option</li>
              <li>The system remembers which sheets need to be finalized and makes them available</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FinalizeMonth;
